import { Box, Flex, Skeleton, Text } from '@chakra-ui/react';
import { Response } from 'datx-jsonapi';
import { useResources, useResourcesInfinite } from 'datx-jsonapi-react';
import { FC, useEffect, useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import HandIcon from '@/assets/icons/ic-hand.svg';

import { MessageBubble } from '@/components/ui/shared/chat/ChatPopup/components/MessageBubble';
import { getUnreadNotificationsQuery } from '@/components/ui/shared/notifications/NotificationBadge/NotificationBadge.queries';
import { ResourceType } from '@/enums/ResourceType';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import useSession from '@/hooks/useSession';
import { queryAllChatMessages } from '@/queries/chat-messages';
import { ChatMessages } from '@/resources/ChatMessages';
import { EventChatChannel } from '@/resources/EventChatChannel';
import { ThinProfile } from '@/resources/ThinProfile';
import { extractSWRInfiniteDataCursor } from '@/utils/swr/extractSWRInfiniteDataCursor';

interface IChatMessages {
  channel?: EventChatChannel;
  profile: ThinProfile;
  setEditMessage: (message: ChatMessages) => void;
}

const PAGE_SIZE = 14;

export const MessagesBox: FC<IChatMessages> = ({ channel, profile, setEditMessage }) => {
  const { eventId } = useQueryParams<{ eventId: string }>({
    eventId: StringParam,
  });

  const { data: chatNotifications } = useResources(getUnreadNotificationsQuery(eventId, ResourceType.EventChatChannel));

  const { data: messages, mutate, error, size, setSize } = useResourcesInfinite(queryAllChatMessages(channel?.id));

  const { isLoadingMore, isReachingEnd, items, isEmpty } = useMemo(
    () => extractSWRInfiniteDataCursor<ChatMessages>(messages as Array<Response<ChatMessages>>, error, size),
    [messages, error, size]
  );

  useEffect(() => {
    if (chatNotifications.length > 0) {
      mutate();
    }
  }, [chatNotifications]);

  if (!channel || isEmpty) {
    return (
      <Flex flexGrow={1} justifyContent="center" alignItems="center">
        <Flex flexDirection="column" alignItems="center" justifyContent="space-between">
          <Flex
            justifyContent="center"
            alignItems="center"
            boxShadow="0px 8px 32px rgba(0, 0, 0, 0.08)"
            borderRadius="50%"
            w={24}
            h={24}
            textColor="violet.500"
          >
            <HandIcon width="48px" height="48px" />
          </Flex>
          <Text mt={7} mb={2} fontWeight="bold" fontFamily="heading" fontSize="xl">
            Say hi to {profile.firstName}
          </Text>
          <Text fontFamily="heading" w="200px" textAlign="center">
            This is a beginning of your conversation with {profile.firstName}
          </Text>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex overflowY="auto" flexDirection="column-reverse" gap={2} mt={4} mb={4} pr={2} flex={1}>
      {items.map((message) => (
        <MessageBubble setEditMessage={setEditMessage} mutate={mutate} key={message.id} message={message} />
      ))}
      <InView
        onChange={(inView) => {
          if (inView && !error && !isReachingEnd) {
            setSize(size + 1);
          }
        }}
      >
        <Box w={1} h={1} />
      </InView>

      {isLoadingMore &&
        Array.from({ length: PAGE_SIZE }).map((_, i) => <Skeleton h={10} borderRadius="4px" w="100%" key={i} />)}
    </Flex>
  );
};
